import { Autocomplete, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { today } from "../../../common/helpers/formatDateTime";
import { fetchTESVariations } from "../../orders/orderAPI";
import { TesVariationForm } from "../../orders/orderTesVariation/TESVariationForm";
import { VariationElement } from "../../orders/types/tesVariationTypes";
import { selectAllCustomerContracts } from "../customerContractsSlice";
import { CustomerContract } from "../customerAPI";
import { handlePricingData } from "../contract/helpers/pricingUtils";
import { getMostRecentPastDateFromArray } from "../contract/helpers/commonUtils";

type Props = {
  edit: boolean;
  handleAreTesVariationsValidChange: (value: boolean) => void;
  contractId?: string;
};

const CollectiveAgreement = ({
  handleAreTesVariationsValidChange,
  contractId,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [elements, setElements] = useState<VariationElement[]>([]);
  const [tesVariationsLoading, setTesVariationsLoading] = useState(false);

  const { t } = useTranslation();

  const {
    control,
    watch,
    getValues,
    formState: { errors },
  } = useFormContext();

  const localizedTes = useAppSelector((state) => state.localized.tes);

  const customerContract: CustomerContract = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  ).find((contract) => contract.id === contractId);
  const pricings = customerContract?.pricing
    ? handlePricingData(customerContract?.pricing)
    : [];
  const currentlyValidPricingDate = getMostRecentPastDateFromArray(
    pricings.map((pricing) => pricing.pricingValidFromDate),
  );
  const tesOptions =
    pricings
      .find(
        (pricing) => pricing.pricingValidFromDate === currentlyValidPricingDate,
      )
      ?.pricingTesList.map((tes: { tesId: string }) => ({
        id: tes.tesId,
        name: localizedTes[tes.tesId],
      })) || [];

  const tes = watch("tes");
  const tesVariation = getValues("tesVariation");

  useEffect(() => {
    if (tes?.id) {
      setTesVariationsLoading(true);
      fetchTESVariations(tes.id, today("yyyy-MM-dd")).then((res) => {
        setElements([...res.elements]);
        setTesVariationsLoading(false);
      });
    }
  }, [tes?.id]);

  const onOpen = () => setMenuOpen(true);
  const onClose = () => setMenuOpen(false);

  return (
    <FormSection title={t("common:collectiveAgreement")}>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography color="textSecondary">{t("common:TES")}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          control={control}
          name="tes"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              open={menuOpen}
              onOpen={onOpen}
              onClose={onClose}
              value={value}
              options={tesOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => onChange(newValue)}
              renderInput={(params) => (
                <CommonTextField
                  {...params}
                  sx={{ maxWidth: "372px" }}
                  error={!!errors.tes}
                />
              )}
            />
          )}
        />
      </Grid>
      {tesVariationsLoading ? (
        <LoadingSpinner sx={{ marginTop: "8px" }} />
      ) : (
        <Controller
          name="tesVariation"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <TesVariationForm
              defaultValues={tesVariation}
              elements={elements}
              fieldProps={{
                sx: {
                  maxWidth: "372px",
                  backgroundColor: "#F9F9FA99",
                },
              }}
              onChange={(newValue, isValid) => {
                //TODO: take isValid property where it needs to go
                handleAreTesVariationsValidChange(isValid);
                onChange(newValue);
              }}
            />
          )}
        />
      )}
    </FormSection>
  );
};

export default CollectiveAgreement;
